import React, { useEffect, useState, FC, useContext } from 'react';
import {
  alpha,
  Box,
  Button,
  createTheme,
  CssBaseline,
  FormControlLabel,
  FormGroup,
  hexToRgb,
  Link,
  Radio,
  RadioGroup,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SurveyService from '../../services/SurveyService';
import { constants } from '../../constants/constants';
import { ISurvey } from '../../shared/interfaces/ISurvey';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import StandardSurveyComment from './StandardSurveyComment';
import { ISurveyStandardQuestion } from '../../shared/interfaces/ISurveyStandardQuestion';
import SurveyUtils from '../../shared/SurveyUtils';
import Autolink from '../Autolink/Autolink';

const StandardSurveyContent: FC<ISurvey> = (props: ISurvey) => {
  const {
    primaryColor,
    secondaryColor,
    platform,
    platformUrl,
    platformName,
    id,
    questions,
    title,
    titleNegative,
    outroText,
    outroTextNegative,
    customFont,
    customFontFormat,
    score,
    introText,
    feedbackActive,
    feedbackTitle,
    feedbackText,
    answers,
    contactEmail,
  } = props;

  const theme = useTheme();
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation();
  const t = localContext ? localContext.t : globalT;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [currentQuestion, setCurrentQuestion] = useState(questions || []);
  const [sum, setSum] = useState<number>(0);
  const { setCurrentView } = useContext(CurrentViewContext);
  const [lastAnswerId, setLastAnswerId] = useState<string>('');
  const [currentScore, setCurrentScore] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>('');
  const [Submitted, setSubmitted] = useState<boolean>(false);
  const [questionsArray, setQuestionsArray] = useState<ISurveyStandardQuestion[]>([
    {
      questionId: currentQuestion[0]?.questionId || '',
      question: currentQuestion[0]?.question || '',
      answer: null,
      feedback: '',
    },
    {
      questionId: currentQuestion[1]?.questionId || '',
      question: currentQuestion[1]?.question || '',
      answer: null,
      feedback: '',
    },
    {
      questionId: currentQuestion[2]?.questionId || '',
      question: currentQuestion[2]?.question || '',
      answer: null,
      feedback: feedback,
    },
  ]);

  const handlePageChange = (change: number) => {
    setCurrentStep((prev) => prev + change);
    if (currentStep === 2) {
      const sum = questionsArray
        .slice(0, 2)
        .reduce((acc, question) => (question.answer ? acc + question.answer : 0), 0);
      setSum(sum);
    }
  };

  const saveSurvey = async () => {
    try {
      const answers = await SurveyService.saveStandardSurveyAnswer(id, questionsArray as []);
      setLastAnswerId(answers[answers.length - 1].ID);
    } catch (error) {
      console.error('Fehler beim Speichern der Umfrage:', error);
    }
  };

  const updateSurvey = async () => {
    try {
      if (lastAnswerId) {
        const allAnswers = questionsArray as [];
        await SurveyService.updateStandardSurveyAnswer(
          lastAnswerId,
          id,
          allAnswers[allAnswers.length - 1]
        );
      }
    } catch (error) {
      console.error('Fehler beim Speichern der Umfrage:', error);
    }
  };

  const getFontFormatByFileExtension = (fontFormat: FontFileExtensions) => {
    return {
      woff2: 'woff2',
      woff: 'woff',
      ttf: 'truetype',
    }[fontFormat];
  };

  const customTheme = createTheme({
    palette: {
      primary: {
        main: primaryColor ? primaryColor : theme.palette.primary.main,
      },
      divider: secondaryColor ? alpha(hexToRgb(secondaryColor), 0.2) : theme.palette.divider,
    },
    typography: {
      fontFamily: customFont && customFontFormat ? `"${id}"` : '"Arial", "Helvetica", sans-serif',
      fontWeightBold: 700,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides:
          customFont && customFontFormat
            ? `
           @font-face {
            weight: 700;
            font-family: "${id}";
            src: url(${customFont}) format("${getFontFormatByFileExtension(
                customFontFormat as FontFileExtensions
              )}");
           }
        `
            : undefined,
      },
    },
  });

  const handleQuestionChange = (questionScore: number, value: number) => {
    setQuestionsArray((prev) => {
      const updatedQuestions = [...prev];
      const question = updatedQuestions[value] ?? { answer: null };
      question.answer = questionScore;
      updatedQuestions[value] = question;
      return updatedQuestions;
    });
  };

  const renderQuestion = (step: number) => {
    if (currentStep === step) {
      return (
        <ThemeProvider theme={customTheme}>
          <CssBaseline />
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: primaryColor,
                marginBottom: theme.spacing(5),
                fontFamily: customFont,
                alignItems: 'center',
                fontWeight: 'bold',
                fontSize: theme.spacing(4),
              }}
            >
              {introText}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: secondaryColor,
                marginBottom: theme.spacing(5),
                fontFamily: customFont,
                alignItems: 'center',
              }}
            >
              {questionsArray[step]?.question}
            </Typography>
            {answers?.map((answer, index) => (
              <Box key={index}>
                <FormGroup>
                  <RadioGroup>
                    <FormControlLabel
                      control={<Radio />}
                      label={answer.answerOne}
                      value={5}
                      onClick={() => handleQuestionChange(5, step)}
                    />
                    <FormControlLabel
                      control={<Radio />}
                      label={answer.answerTwo}
                      value={4}
                      onClick={() => handleQuestionChange(4, step)}
                    />
                    <FormControlLabel
                      control={<Radio />}
                      label={answer.answerThree}
                      value={3}
                      onClick={() => handleQuestionChange(3, step)}
                    />
                    <FormControlLabel
                      control={<Radio />}
                      label={answer.answerFour}
                      value={2}
                      onClick={() => handleQuestionChange(2, step)}
                    />
                    <FormControlLabel
                      control={<Radio />}
                      label={answer.answerFive}
                      value={1}
                      onClick={() => handleQuestionChange(1, step)}
                    />
                  </RadioGroup>
                </FormGroup>
              </Box>
            ))}
          </Box>
        </ThemeProvider>
      );
    }
  };

  const renderLastQuestion = (step: number) => {
    if (currentStep === step) {
      return (
        <ThemeProvider theme={customTheme}>
          <CssBaseline />
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: primaryColor,
                marginBottom: theme.spacing(5),
                fontFamily: customFont,
                alignItems: 'center',
                fontWeight: 'bold',
                fontSize: theme.spacing(4),
              }}
            >
              {introText}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: secondaryColor,
                marginBottom: theme.spacing(5),
                fontFamily: customFont,
                alignItems: 'center',
              }}
            >
              {questionsArray[step]?.question}
            </Typography>
            <FormGroup>
              <RadioGroup>
                <FormControlLabel
                  control={<Radio />}
                  label={t('yes')}
                  value={1}
                  onClick={() => handleQuestionChange(1, step)}
                />
                <FormControlLabel
                  control={<Radio />}
                  label={t('no')}
                  value={0}
                  onClick={() => handleQuestionChange(0, step)}
                />
              </RadioGroup>
            </FormGroup>
          </Box>
        </ThemeProvider>
      );
    }
  };

  const renderPositivOutro = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: primaryColor,
            marginBottom: theme.spacing(5),
            fontWeight: 'bold',
            fontFamily: customFont,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: secondaryColor,
            marginBottom: theme.spacing(5),
            fontFamily: customFont,
            alignItems: 'center',
          }}
        >
          <Autolink text={outroText} />
        </Typography>
        {platform && platformUrl && (
          <>
            <Link
              href={platformUrl}
              underline={'none'}
              target={'_blank'}
            >
              <Typography
                variant="h5"
                sx={{
                  color: '#fff',
                  backgroundColor: primaryColor,
                  padding: '.2em .5em',
                  fontWeight: 'light',
                  borderRadius: '4px',
                  boxShadow:
                    'rgba(0, 0, 0, 0.2) 0px 3px 1px -2px, rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px;',
                  mb: theme.spacing(2),
                  fontFamily: customFont,
                  '&:hover': {
                    opacity: 0.9,
                  },
                }}
              >
                {`${t('please_rate_us')} ${SurveyUtils.getName(platformName)}!`}
              </Typography>
            </Link>
            <Link
              href={platformUrl}
              variant="body1"
              underline="none"
              target="_blank"
              sx={{
                color: primaryColor,
                fontWeight: 'bold',
                fontFamily: customFont,
                textAlign: 'center',
              }}
            >
              <Box
                component={'img'}
                sx={{
                  width: '100%',
                  maxWidth: '256px',
                  height: 'auto',
                  minHeight: '64px',
                  mb: theme.spacing(2),
                }}
                src={constants.platform_logos[platform.toLowerCase()]}
                alt={`${platform} Logo`}
              />
            </Link>
          </>
        )}
      </Box>
    );
  };

  const renderNegativOutro = () => {
    let tmpTitle = titleNegative;
    if (!tmpTitle) {
      tmpTitle = title;
    }
    let tmpOutroText = outroTextNegative;
    if (!tmpOutroText) {
      tmpOutroText = outroText;
    }
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: primaryColor,
            marginBottom: theme.spacing(5),
            fontWeight: 'bold',
            fontFamily: customFont,
          }}
        >
          {tmpTitle}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: secondaryColor,
            marginBottom: theme.spacing(5),
            fontFamily: customFont,
            alignItems: 'center',
          }}
        >
          <Autolink text={tmpOutroText} />
        </Typography>
        {contactEmail && !feedbackActive && (
          <Typography
            variant="h5"
            sx={{
              color: secondaryColor,
              marginBottom: theme.spacing(3),
              fontFamily: customFont,
              alignItems: 'center',
              mt: 4,
            }}
          >
            {contactEmail ? `${t('appreciate_feedback')}: ${contactEmail}` : null}
          </Typography>
        )}
      </Box>
    );
  };

  const renderFeedbackComment = () => {
    let tmpTitle = titleNegative;
    if (!tmpTitle) {
      tmpTitle = title;
    }
    return (
      <>
        <StandardSurveyComment
          {...{
            secondaryColor,
            primaryColor,
            customFont,
            feedbackText,
            feedbackTitle,
            title: tmpTitle,
          }}
          onChange={setFeedback}
        />
        <Box
          display="flex"
          justifyContent="flex-end"
          width="100%"
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            onClick={() => {
              updateSurvey();
              setSubmitted(true);
              handlePageChange(1);
            }}
          >
            {t('send')}
          </Button>
        </Box>
      </>
    );
  };

  useEffect(() => {
    setCurrentView(t('survey'));
    document.title = `Reputativ ${t('survey')}`;
  }, [setCurrentView, t]);

  useEffect(() => {
    if (questions) {
      setCurrentScore(questions[0].score);
    }
  }, [score]);

  useEffect(() => {
    questionsArray[2].feedback = feedback;
  }, [feedback]);

  useEffect(() => {
    const newSum = questionsArray
      .slice(0, 2)
      .reduce((acc, question) => acc + (question.answer || 0), 0);
    setSum(newSum);
  }, [questionsArray, sum]);

  const buttonText =
    (questionsArray[2]?.answer === 1 &&
      currentStep === 2 &&
      feedbackActive &&
      currentScore <= (questionsArray[0]?.answer ?? 0) + (questionsArray[1]?.answer ?? 0)) ||
    (currentStep === 2 && !feedbackActive)
      ? t('send')
      : t('next');

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        width: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        alignSelf: 'center',
        ml: isSmallScreen ? '1%' : '10%',
        mr: isSmallScreen ? '1%' : '10%',
        mt: 'auto',
      }}
    >
      {renderQuestion(0)}
      {renderQuestion(1)}
      {renderLastQuestion(2)}
      {currentStep === 3 && sum >= currentScore && questionsArray[2].answer === 1 && Submitted
        ? renderPositivOutro()
        : null}
      {feedbackActive && !Submitted && currentStep === 3 ? renderFeedbackComment() : null}
      {(Submitted && questionsArray[2].answer === 0) || (sum < currentScore && Submitted)
        ? renderNegativOutro()
        : null}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: currentStep === 0 || currentStep === 3 ? 'flex-end' : 'space-between',
          alignItems: 'center',
          textAlign: 'center',
          width: '100%',
          mt: theme.spacing(4),
        }}
      >
        {currentStep < 3 && currentStep !== 0 && (
          <Button
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            onClick={() => handlePageChange(-1)}
          >
            {t('back')}
          </Button>
        )}
        {currentStep < 3 && (
          <Button
            variant="contained"
            disabled={questionsArray[currentStep]?.answer === null}
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            onClick={() => {
              if (currentStep == 2 || buttonText === t('send')) {
                saveSurvey();
              }
              if (buttonText === t('send')) {
                setSubmitted(true);
              }
              handlePageChange(1);
            }}
          >
            {buttonText}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default StandardSurveyContent;
